import React, { useEffect, useState } from "react";
import { Field, InjectedFormProps, reduxForm } from "redux-form";
import { login } from "../user/UserActions";
import { AppMode, LoginVerification, ManualLoginRequest, UserLoginPrincipal } from "../http/protocol";
import { Dispatch } from "redux";
import { ShowHidePassword } from "../user/ui/ShowHidePassword";
import { t } from "../i18n";
import { connect, useSelector } from "react-redux";
import { AppState } from "../RootReducer";
import { withRouter } from "react-router";
import { SettingsState } from "../settings/SettingsReducer";
import { renderCheckbox, renderPhoneNumberField } from "../user/ui/form";
import { LoginStatus } from "../http/Types";
import { MFAState } from "../user/UserReducer";
import { isMobile } from "react-device-detect";
import { BrandButton } from "./buttons/buttons";
import * as H from "history";
import { normalizePhone } from "../user/ui/utils";
import { PrincipalSelector } from "../onboardingpage/PrincipalSelector";
import { PHONE_INPUT_CLASSNAME, PHONE_INPUT_TABINDEX, setTabIndex } from "../utils/tabindex";
import { useGetQueryParamValues } from "../onboardingpage/modalview/utils";

type HTMLInputEvent = React.ChangeEvent<HTMLInputElement>;
interface Props extends InjectedFormProps {
  remoteError?: string;
  text?: string;
  loggedIn: boolean;
  settings?: SettingsState;
  country?: string;
  homescreen?: boolean;
  appMode?: AppMode;
  emailVerified: boolean;
  status?: LoginStatus;
  errorMessage?: string;
  mfa?: MFAState;
  previousPaths?: string[];
  location: H.Location<any>;
  loginPrincipals?: UserLoginPrincipal[];
  dispatch?: Dispatch;
  disableInputEmail?: boolean;
  disableInputPhone?: boolean;
}

export const EMAIL_QUERY_PARAM = "email";
export const PHONE_NUMBER_QUERY_PARAM = "phoneNumber";
export const VERIFICATION_CHANNEL_PARAM = "verificationChannel";

function formatVerificationChannel(verificationChannel: LoginVerification): UserLoginPrincipal {
  switch (verificationChannel) {
    case LoginVerification.EMAIL:
      return UserLoginPrincipal.EMAIL;
    case LoginVerification.PHONE:
      return UserLoginPrincipal.PHONE_NUMBER;
    default:
      return UserLoginPrincipal.EMAIL;
  }
}

const LoginForm = ({ country, loginPrincipals, handleSubmit, status, error, dispatch, homescreen, submitting, change, disableInputEmail, disableInputPhone }: Props) => {
  const [inputEvent, setInputEvent] = useState<string>("");
  const phoneNumber = useSelector((state: any) => state.form.login?.values?.phoneNumber);
  const email = useSelector((state: any) => state.form.login?.values?.email);
  const password = useSelector((state: any) => state.form.login?.values?.password);
  const principal = useSelector((state: any) => state.form.login?.values?.principal);
  const phoneIsValid = principal === UserLoginPrincipal.PHONE_NUMBER && phoneNumber && phoneNumber.length > 5;
  const emailIsValid = principal === UserLoginPrincipal.EMAIL && email && email.length > 5;
  const isPrincipalValid = emailIsValid || phoneIsValid;
  const shouldDisableSubmit = !password || !isPrincipalValid || submitting;
  const [prefilledEmail, prefilledPhoneNumber, verificationChannel] = useGetQueryParamValues([EMAIL_QUERY_PARAM, PHONE_NUMBER_QUERY_PARAM, VERIFICATION_CHANNEL_PARAM])();

  useEffect(() => {
    if (principal === UserLoginPrincipal.PHONE_NUMBER) {
      setTabIndex(PHONE_INPUT_CLASSNAME, PHONE_INPUT_TABINDEX);
    }
  }, [principal]);

  useEffect(() => {
    if (verificationChannel != null) change("principal", formatVerificationChannel(verificationChannel as LoginVerification));
    change("email", prefilledEmail);
    change("phoneNumber", prefilledPhoneNumber);
  }, []);

  const handleFocus = (event: HTMLInputEvent) => {
    setInputEvent(event.type);
  };

  return (
    <form className={`form login ${inputEvent == "focus" ? "login-form-focus" : ""}`} onSubmit={handleSubmit}>
      {error && <div className={"alert alert-danger"}>{t(error)}</div>}
      {(status == LoginStatus.MFA_VERIFICATION_REQUIRED || status == LoginStatus.MFA_VERIFICATION_ERROR) && (
        <div>
          {status == LoginStatus.MFA_VERIFICATION_ERROR && <div className={"alert alert-danger"}>{t("forms.verificationCode.error")}</div>}
          <div className={"form-group"}>
            <label>{t("forms.verificationCode.help")}</label>
            <Field onFocus={handleFocus} onBlur={handleFocus} placeholder={t("forms.verificationCode.label")} className={"form-control"} name="mfaVerificationCode" component="input" type="text" autoComplete={"verificationCode"} />
          </div>
        </div>
      )}
      {status != LoginStatus.MFA_VERIFICATION_REQUIRED && status != LoginStatus.MFA_VERIFICATION_ERROR && (
        <div>
          {(loginPrincipals ?? []).length > 1 && (
            <div className={"form-group-container"}>
              <PrincipalSelector principals={loginPrincipals ?? []} principal={principal} setPrincipal={(p: UserLoginPrincipal) => change("principal", p)} />
            </div>
          )}

          {principal == UserLoginPrincipal.EMAIL && (
            <div className={"form-group"}>
              <Field onFocus={handleFocus} onBlur={handleFocus} placeholder={t("forms.username.label")} className={"form-control"} name="email" component="input" type="text" autoComplete={"email"} disabled={disableInputEmail} />
            </div>
          )}
          {principal == UserLoginPrincipal.PHONE_NUMBER && (
            <Field
              props={{ label: "PhoneNumber", nameOverride: "phoneNumber" }}
              name="phoneNumber"
              value=""
              hideHelp={true}
              focus={handleFocus}
              blur={handleFocus}
              pristine={true}
              country={country?.toLowerCase() ?? ""}
              component={renderPhoneNumberField}
              type="text"
              disabled={disableInputPhone}
            />
          )}

          <ShowHidePassword>
            <Field onFocus={handleFocus} onBlur={handleFocus} placeholder={t("forms.password.label")} className={"form-control"} name="password" component="input" type="password" autoComplete={"password"} />
          </ShowHidePassword>
        </div>
      )}
      <div style={{ display: "none" }}>
        <Field name={"principal"} component={"input"} />
      </div>
      <BrandButton className={`btn btn-cta full-width ${inputEvent === "focus" ? "btn-focus" : ""}`} loading={submitting} disabled={shouldDisableSubmit} type={"submit"}>
        {t("account.login")}
      </BrandButton>

      {homescreen && (
        <div className="form-group" style={{ alignSelf: "center", marginTop: "1rem" }}>
          <Field props={{ label: t("forms.rememberMe") }} name="generateLoginToken" hideHelp={"true"} component={renderCheckbox} type="checkbox" required={false} />
        </div>
      )}
    </form>
  );
};

const mapStateToProps = ({ user, settings, navigation, journey }: AppState) => {
  return {
    mfa: user.user && user.mfa,
    status: user.user && user.status,
    errorMessage: user.user && user.user.errorMessage,
    loggedIn: user.user && user.user.loggedIn,
    settings: settings,
    country: user.user?.country,
    emailVerified: user.user && user.user.emailVerified,
    previousPaths: navigation.previousPaths ?? [],
    loginPrincipals: journey.journey?.journey?.userLoginPrincipals ?? [],
    appMode: user.appMode,
    initialValues: {
      generateLoginToken: isMobile,
      principal: journey.journey?.journey?.userLoginPrincipals?.[0] ?? UserLoginPrincipal.EMAIL,
    },
  };
};

const form = reduxForm<any, ManualLoginRequest>({
  form: "login",
  onSubmit: (req: ManualLoginRequest & { principal?: UserLoginPrincipal }, dispatch: Dispatch, props: any) => {
    req = { ...req, appMode: props.appMode };
    if (req.principal == UserLoginPrincipal.EMAIL) {
      delete req["phoneNumber"];
    } else {
      delete req["email"];
      req = { ...req, phoneNumber: normalizePhone(req.phoneNumber) };
    }
    delete req["principal"];

    if (props.mfa && (props.status == LoginStatus.MFA_VERIFICATION_REQUIRED || props.status == LoginStatus.MFA_VERIFICATION_ERROR)) {
      req = {
        ...req,
        mfaProvider: props.mfa.provider,
        email: props.mfa.username,
        password: props.mfa.password,
        mfaVerificationCode: parseInt("" + req.mfaVerificationCode),
      };
    }
    if (!props.homescreen) {
      req.generateLoginToken = false;
    }

    return dispatch(login(req) as any);
  },
})(LoginForm as any);

export default connect(mapStateToProps, {})(withRouter(form as any) as any) as any;
